import React from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { useRouter } from 'next/router'

import { actionCreators as providerProfilePageActions } from 'store/modules/ProviderProfilePage'
import { withApollo } from 'helpers/graphql'

import EventLocationProfilePage from './EventLocation'
import EventIdeaProfilePage from './EventIdea'
import EventSupplierProfilePage from './EventSupplier'
import EventRoomProfilePage from './EventRoom'

import Header from 'components/Header'
import Spinner from 'components/Spinner'
import PageHead from 'containers/PageHead'
import ProviderSchemaOrg from 'components/SchemaOrg'
import { useCollectionsQuery } from 'hooks/collections/graphql.generated'

import { pushDataLayer } from 'helpers/googleAnalytics'
import { addQueryStringParamsToUrl } from 'helpers/params'

import './index.less'

const mapStateToProps = (state) => ({
  ...state.providerProfilePage,
})

const mapDispatchToProps = {
  ...providerProfilePageActions,
}

class ProviderProfilePage extends React.PureComponent {
  componentDidMount () {
    this.pushProductDetailEvent()
  }

  componentDidUpdate (prevProps) {
    if (this.props.provider.id !== prevProps.provider.id) {
      this.pushProductDetailEvent()
    }
  }

  pushProductDetailEvent = () => {
    pushDataLayer({
      event: 'eeProductDetail',
    })
  }

  onProjectSelect = (projectId) => {
    const { router } = this.props

    const url = addQueryStringParamsToUrl(window.location, { projectId }, { removeEmptyParams: true })

    router.push(url)
  }

  render () {
    const {
      provider,
      loading,
      headerData,
    } = this.props

    if (loading) {
      return <Spinner fullPageHeight />
    }

    if (provider.id === null) { return null }

    return (
      <article className="provider-profile-page">
        {headerData && (
          <PageHead headerData={headerData}>
            {provider.pictures?.[0]?.url && (
              <>
                <meta property="og:image" content={provider.pictures[0].url} />
                <meta name="thumbnail" content={provider.pictures[0].url} />
              </>
            )}
          </PageHead>
        )}

        <Header alreadyWrappedWithApollo onProjectSelect={this.onProjectSelect} />

        <ProviderProfileContent
          provider={provider}
          headerData={headerData}
        />

        <ProviderSchemaOrg provider={provider} />
      </article>
    )
  }
}

const ProviderProfileContent = ({ provider, headerData }) => {
  const router = useRouter()
  const { isSignedIn, collectionsEnabled } = useSelector(state => ({
    isSignedIn: state.currentUser.isSignedIn,
    collectionsEnabled: state.currentUser.collectionsEnabled,
  }))
  const { data: collectionsData } = useCollectionsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isSignedIn || !collectionsEnabled,
    ssr: false,
  })
  const collections = collectionsData?.collections || []
  const projectId = Number(router?.query?.projectId)
  const props = { collections, projectId, provider, headerData }

  if (provider.type === 'EventLocation') {
    return <EventLocationProfilePage {...props} />
  }

  if (provider.type === 'EventIdea') {
    return <EventIdeaProfilePage {...props} />
  }

  if (provider.type === 'EventSupplier') {
    return <EventSupplierProfilePage {...props} />
  }

  if (provider.type === 'EventRoom') {
    return <EventRoomProfilePage {...props} />
  }

  return null
}

ProviderProfileContent.propTypes = {
  provider: PropTypes.object.isRequired,
  headerData: PropTypes.object,
}

ProviderProfilePage.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  provider: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  headerData: PropTypes.object,
}

const ConnectedProviderProfilePage = connect(mapStateToProps, mapDispatchToProps)(ProviderProfilePage)

// `next-with-apollo` swallows the `router` prop (it destructures it out for its own
// client factory and forwards only the rest), so the router must be read from the
// context *below* it with `useRouter` rather than injected from above by `withRouter`.
const ProviderProfilePageWithRouter = (props) => (
  <ConnectedProviderProfilePage {...props} router={useRouter()} />
)

export default withApollo(ProviderProfilePageWithRouter)
